import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { SegmentedControl } from '@client/components/_ui/data-entry/SegmentedControl';
import { TaggedList } from '@client/components/_ui/display/TaggedList';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { Space } from '@client/components/_ui/layout/Space';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Typography } from 'antd';
import { List, Map as MapIcon, Store } from 'lucide-react';
import { lazy, Suspense, useMemo, useState } from 'react';
import { SuppliersTabs } from '../SuppliersTabs';
import { SupplierFilters } from './SupplierFilters';

const SuppliersMap = lazy(() => import('./SuppliersMap'));

/** Upper bound for the map view — pins for every matching supplier, not one table page. */
const MAP_RESULTS_LIMIT = 1000;

export const Route = createFileRoute('/console/$orgId/configuration/vendor-management/suppliers/list/')({
    staticData: {
        metadata: {
            title: 'Suppliers list',
        },
        ui: {
            icon: Store,
            title: 'Suppliers configuration',
            subtitle: 'Manage supplier details and settings',
            menuDescription: 'Manage supplier details and settings',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <SuppliersTabs active="list" />
            <SupplierSearch />
        </PageController>
    );
}

type QueryParams = RouterInputs['supplier']['listSuppliers'];

const columnHelper = createColumnHelper<
    RouterOutputs['supplier']['listSuppliers']['rows'][number],
    QueryParams['orderBy']
>();

function SupplierSearch() {
    const orgId = useOrgId();
    const navigate = useNavigate();

    const [view, setView] = useState<'list' | 'map'>('list');
    const [input, setInput] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
    });

    const { data, isPending, refetch } = trpc.supplier.listSuppliers.useQuery(input);

    // The table query is paginated; the map wants every matching supplier.
    const mapQuery = trpc.supplier.listSuppliers.useQuery(
        { where: input.where, limit: MAP_RESULTS_LIMIT },
        { enabled: view === 'map' },
    );
    const mapRows = mapQuery.data?.rows ?? [];
    // Memoised so the map only refits when the data actually changes
    const pins = useMemo(
        () =>
            (mapQuery.data?.rows ?? [])
                .filter((row) => row.geocodeStatus === 'ok' && row.latitude != null && row.longitude != null)
                .map((row) => ({
                    id: row.id.toString(),
                    name: row.name,
                    category: row.category,
                    suburb: row.suburb,
                    latitude: row.latitude as number,
                    longitude: row.longitude as number,
                })),
        [mapQuery.data],
    );
    const unmappedCount = mapRows.length - pins.length;
    const totalMatching = mapQuery.data?.total ?? 0;
    const mapTruncated = totalMatching > mapRows.length;

    const openSupplier = (id: string) => {
        navigate({
            to: '/console/$orgId/configuration/vendor-management/suppliers/list/details/$id',
            params: { orgId, id: BigInt(id) },
        });
    };

    const tableProps = usePrismaTable({
        data: data?.rows,
        total: data?.total,
        loading: isPending,
        rowKey: 'id',
        queryParams: input,
        onParamsChange: setInput,
        columnVisibility: {
            enabled: true,
            persistKey: 'suppliers-table-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
        columns: [
            columnHelper.accessor('id', {
                id: 'id',
                header: 'ID',
                sortable: 'id',
                fixed: 'left',
                hideable: false,
            }),
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Name',
                sortable: 'name',
            }),
            columnHelper.accessor('category', {
                id: 'category',
                header: 'Category',
                sortable: 'category',
            }),
            columnHelper.display({
                id: 'contact',
                header: 'Contact',
                cell: ({ row }) =>
                    row.mainContact
                        ? [
                              [row.mainContact.firstName, row.mainContact.lastName].filter(Boolean).join(' '),
                              row.mainContact.phone,
                          ]
                              .filter(Boolean)
                              .join(' · ')
                        : '',
            }),
            columnHelper.display({
                id: 'handlingParties',
                header: 'Can work for',
                cell: ({ row }) => (
                    <TaggedList
                        items={row.HandlingParties || []}
                        accessor={(item) => item.name}
                        additionalItemsTitle="Additional handling parties:"
                        displayLimit={3}
                        tagProps={{
                            className: 'text-sm',
                            maxWidth: '150px',
                        }}
                        emptyText=""
                    />
                ),
            }),
            columnHelper.actions({
                cell: ({ row }) => (
                    <Button.OpenInNewTab
                        to="/console/$orgId/configuration/vendor-management/suppliers/list/details/$id"
                        params={{
                            orgId,
                            id: row.id,
                        }}
                    />
                ),
            }),
        ],
    });

    return (
        <>
            <TableToolbar
                filters={<SupplierFilters onChange={(where) => setInput((prev) => ({ ...prev, where, offset: 0 }))} />}
                actions={
                    <Space>
                        <SegmentedControl<'list' | 'map'>
                            aria-label="Switch between list and map view"
                            value={view}
                            onChange={setView}
                            options={[
                                { value: 'list', label: 'List', icon: <List size={14} /> },
                                { value: 'map', label: 'Map', icon: <MapIcon size={14} /> },
                            ]}
                        />
                        <Button.Add.Link
                            label="New supplier"
                            to="/console/$orgId/configuration/vendor-management/suppliers/add"
                            params={{ orgId }}
                            variant="primary"
                        />
                    </Space>
                }
            />

            {view === 'map' ? (
                mapQuery.isPending ? (
                    <Loading />
                ) : (
                    <div className="flex flex-col gap-2">
                        <Suspense fallback={<Loading />}>
                            <SuppliersMap pins={pins} onOpenSupplier={openSupplier} />
                        </Suspense>
                        {mapTruncated && (
                            <Typography.Text type="secondary">
                                Showing the first {mapRows.length} of {totalMatching} matching suppliers — refine the
                                filters to see the rest.
                            </Typography.Text>
                        )}
                        {unmappedCount > 0 && (
                            <Typography.Text type="secondary">
                                {unmappedCount} supplier{unmappedCount === 1 ? '' : 's'} without a map location{' '}
                                {unmappedCount === 1 ? 'is' : 'are'} shown in the list view only.
                            </Typography.Text>
                        )}
                    </div>
                )
            ) : (
                <PrismaTable
                    {...tableProps}
                    onRow={({ id }) => ({
                        className: 'cursor-pointer',
                        onClick(e) {
                            navigate(
                                {
                                    to: '/console/$orgId/configuration/vendor-management/suppliers/list/details/$id',
                                    params: {
                                        orgId,
                                        id,
                                    },
                                },
                                e,
                            );
                        },
                    })}
                />
            )}
        </>
    );
}
